import React, { useState, useEffect } from 'react';
import { Image as ImageIcon, X, ZoomIn, Film, Sparkles, Landmark, Flame } from 'lucide-react';
import '../../styles/HomamPortal.css';
import { MOCK_GALLERY_ASSETS } from '../../data/homamData';
import { mergeGalleryPhotos, resolveGalleryImageSrc } from '../../data/galleryData';
import { API_ENDPOINTS } from '../../config/api';
import HomamSubNavbar from '../../components/HomamSubNavbar';
import HomamFooter from '../../components/HomamFooter';

const getCategoryIcon = (cat) => {
  const lower = (cat || '').toLowerCase();
  if (lower === 'all') return <Sparkles className="w-4 h-4" />;
  if (lower.includes('video')) return <Film className="w-4 h-4" />;
  if (lower.includes('temple') || lower.includes('mandir') || lower.includes('darshan')) return <Landmark className="w-4 h-4" />;
  if (lower.includes('homam') || lower.includes('puja') || lower.includes('yajna') || lower.includes('agni') || lower.includes('aarti') || lower.includes('pooja')) return <Flame className="w-4 h-4" />;
  return <ImageIcon className="w-4 h-4" />;
};

const mapPhotoItem = (p, idx) => ({
  id: p.id || p._id || `photo-${idx}`,
  title: p.title || `Divine Moment ${idx + 1}`,
  category: p.displayCategory || (p.category ? p.category.charAt(0).toUpperCase() + p.category.slice(1) : 'Temple Darshan'),
  rawCategory: (p.category || 'temple').toLowerCase(),
  url: resolveGalleryImageSrc(p.url || p.src),
  rawSrc: p.src || p.url,
  caption: p.caption || p.title || 'Sri Gnana Sai Baba Mandir Sacred Darshan',
  altText: p.altText || p.title || 'Sri Gnana Sai Baba Mandir'
});

const HOMAM_VIDEOS = [
  {
    id: 'promo-1',
    title: 'Sri 27 Nakshatra Maha Homam — Official Promo 1',
    tamilTitle: 'ஸ்ரீ 27 நட்சத்திர மகா ஹோமம் திருமுறை அழைப்பிதழ்',
    src: '/videos/promo1.mp4',
    description: 'Divine invitation to the sacred 27 Consecutive Days Maha Homam by Guruji at Sri Gnana Sai Baba Mandir Sanctum Arena.'
  },
  {
    id: 'promo-2',
    title: 'Sri 27 Nakshatra Maha Homam — Darshan Promo 2',
    tamilTitle: 'ஸ்ரீ மகா ஹோமம் சிறப்பு தரிசனம்',
    src: '/videos/promo2.mp4',
    description: 'Glimpses of sacred Ahutis, Vedic chanting, and Janma Nakshatra blessing procedures.'
  }
];

export function HomamGallery() {
  const [activeCategory, setActiveCategory] = useState('All');
  const [lightboxAsset, setLightboxAsset] = useState(null);
  const [photos, setPhotos] = useState(() => MOCK_GALLERY_ASSETS.map(mapPhotoItem));

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
    document.title = 'Sacred Photo & Video Gallery | Sri 27 Nakshatra Maha Homam';

    const fetchPhotos = async () => {
      try {
        const res = await fetch(API_ENDPOINTS.GALLERY);
        if (res.ok) {
          const data = await res.json();
          // Merge backend photos with local photos without duplication
          const merged = mergeGalleryPhotos(data.photos || []);
          setPhotos(merged.map(mapPhotoItem));
        }
      } catch (err) {
        console.error('Failed to fetch gallery photos:', err);
      }
    };

    fetchPhotos();
  }, []);

  const displayPhotos = photos;

  // Build unique categories from actual photos
  const photoCategories = Array.from(new Set(displayPhotos.map((p) => p.category))).filter(Boolean);
  const categoriesList = ['All', 'Sacred Videos', ...(photoCategories.length > 0 ? photoCategories : ['Temple Darshan'])];

  const filteredAssets =
    activeCategory === 'All'
      ? displayPhotos
      : activeCategory === 'Sacred Videos'
      ? []
      : displayPhotos.filter((a) => a.category?.toLowerCase() === activeCategory.toLowerCase());

  const showVideos = activeCategory === 'All' || activeCategory === 'Sacred Videos';

  return (
    <div className="homam-portal-wrapper">
      <HomamSubNavbar />

      {/* Header */}
      <section className="homam-hero-sec" style={{ padding: '40px 0 50px 0' }}>
        <div className="homam-container">
          <div className="homam-emblem-badge" style={{ marginBottom: 10 }}>
            <ImageIcon className="w-3.5 h-3.5" style={{ color: '#b8860b' }} />
            <span>Sacred Visual Darshan</span>
          </div>

          <h1 style={{ fontSize: '2.5rem', fontWeight: 900, color: '#0f2d5c', margin: '0 0 8px 0' }}>
            Sacred Photo & Video Gallery
          </h1>

          <p className="homam-hero-tamil" style={{ margin: '0 0 10px 0' }}>
            ஸ்ரீ ஞான சாய் பாபா மந்திர் திருக்காட்சிகள் & காணொளிகள்
          </p>

          <p style={{ fontSize: 13.5, color: '#475569', maxWidth: 640, margin: '0 auto' }}>
            Experience divine glimpses of Guruji, Sri Gnana Sai Baba Mandir, Agni Kunda Ahutis, official promo videos, and joyful devotee congregations.
          </p>
        </div>
      </section>

      {/* Category Tabs */}
      <section className="homam-gallery-filter-wrap">
        <div className="homam-container" style={{ display: 'flex', justifyContent: 'center' }}>
          <div className="homam-gallery-filter-container">
            {categoriesList.map((cat) => {
              const count =
                cat === 'All'
                  ? displayPhotos.length + HOMAM_VIDEOS.length
                  : cat === 'Sacred Videos'
                  ? HOMAM_VIDEOS.length
                  : displayPhotos.filter((a) => a.category?.toLowerCase() === cat.toLowerCase()).length;

              const isActive = activeCategory === cat;

              return (
                <button
                  key={cat}
                  type="button"
                  onClick={() => setActiveCategory(cat)}
                  className={`homam-gallery-tab-btn ${isActive ? 'active' : ''}`}
                  aria-pressed={isActive}
                >
                  <span className="tab-icon">
                    {getCategoryIcon(cat)}
                  </span>
                  <span>{cat}</span>
                  <span className="homam-gallery-tab-badge">
                    {count}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sacred Videos Showcase Section */}
      {showVideos && (
        <section style={{ padding: '24px 0 10px 0' }}>
          <div className="homam-container">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
              <Film className="w-5 h-5" style={{ color: '#b8860b' }} />
              <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f2d5c', margin: 0 }}>
                Sacred Maha Homam Promo & Darshan Videos
              </h2>
            </div>

            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 20, justifyContent: 'center', marginBottom: 28 }}>
              {HOMAM_VIDEOS.map((video) => (
                <div
                  key={video.id}
                  style={{
                    width: '100%',
                    maxWidth: 360,
                    background: '#ffffff',
                    borderRadius: 16,
                    overflow: 'hidden',
                    border: '1px solid #EADDC7',
                    boxShadow: '0 4px 16px rgba(0,0,0,0.06)',
                    display: 'flex',
                    flexDirection: 'column'
                  }}
                >
                  <div style={{ background: '#0a192f', position: 'relative', width: '100%', height: 200, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <video
                      controls
                      preload="none"
                      onError={(e) => {
                        e.target.style.display = 'none';
                        const parent = e.target.parentElement;
                        if (parent && !parent.querySelector('.video-fallback-msg')) {
                          const fallback = document.createElement('div');
                          fallback.className = 'video-fallback-msg';
                          fallback.style.cssText = 'color: #ffd86b; font-size: 12px; font-weight: 700; text-align: center; padding: 20px;';
                          fallback.innerHTML = '🎬 Divine Video Darshan<br/><span style="color:#ffffff; font-size: 11px; font-weight: 400; opacity: 0.8;">Video stream will be available shortly</span>';
                          parent.appendChild(fallback);
                        }
                      }}
                      style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                    >
                      <source src={video.src} type="video/mp4" />
                      Your browser does not support the video tag.
                    </video>
                  </div>
                  <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 4, flex: 1, textAlign: 'left' }}>
                    <span className="homam-video-glass-badge">
                      🎬 Official Promo
                    </span>
                    <h3 style={{ fontSize: 13.5, fontWeight: 700, color: '#0f2d5c', margin: '2px 0 0 0', lineHeight: 1.3 }}>
                      {video.title}
                    </h3>
                    <p style={{ fontSize: 11.5, fontWeight: 600, color: '#b8860b', margin: 0 }}>
                      {video.tamilTitle}
                    </p>
                    <p style={{ fontSize: 11.5, color: '#64748b', margin: '2px 0 0 0', lineHeight: 1.4 }}>
                      {video.description}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Photo Media Grid */}
      {filteredAssets.length > 0 && (
        <section className="homam-grid-wrap" style={{ paddingTop: showVideos ? 10 : 30 }}>
          <div className="homam-container">
            {showVideos && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16 }}>
                <ImageIcon className="w-5 h-5" style={{ color: '#b8860b' }} />
                <h2 style={{ fontSize: '1.4rem', fontWeight: 800, color: '#0f2d5c', margin: 0 }}>
                  Sacred Photo Darshan
                </h2>
              </div>
            )}
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'center', gap: 24 }}>
              {filteredAssets.map((asset) => (
                <div
                  key={asset.id}
                  onClick={() => setLightboxAsset(asset)}
                  style={{ flex: '1 1 300px', maxWidth: 360, minWidth: 260, position: 'relative', borderRadius: 24, overflow: 'hidden', border: '1px solid #EADDC7', background: '#0a192f', height: 240, cursor: 'pointer', boxShadow: '0 4px 18px rgba(0,0,0,0.06)', boxSizing: 'border-box' }}
                >
                  <img
                    src={asset.url}
                    alt={asset.altText}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      const baseName = asset.rawSrc?.split("-").pop() || asset.rawSrc?.split("/").pop() || asset.url?.split("/").pop();
                      if (baseName && !e.target.src.includes(`/images/${baseName}`)) {
                        e.target.src = `/images/${baseName}`;
                      } else {
                        e.target.src = "/images/placeholder.jpg";
                      }
                    }}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                  <div style={{ position: 'absolute', inset: 0, background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.25) 60%, transparent 100%)', display: 'flex', flexDirection: 'column', justifyContent: 'flex-end', padding: 16, textAlign: 'left' }}>
                    <span className="homam-image-glass-badge">
                      {asset.category}
                    </span>
                    <p style={{ color: '#ffffff', fontSize: 12.5, fontWeight: 600, margin: 0, lineHeight: 1.3 }}>
                      {asset.caption}
                    </p>
                  </div>
                  <div className="homam-image-glass-zoom" title="Click to view full photo">
                    <ZoomIn className="w-4 h-4" />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Lightbox Modal: Image Only (No Blue Header) */}
      {lightboxAsset && (
        <div
          onClick={() => setLightboxAsset(null)}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.92)',
            zIndex: 99999,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 16,
          }}
        >
          {/* Floating close button */}
          <button
            type="button"
            onClick={() => setLightboxAsset(null)}
            style={{
              position: 'fixed',
              top: 24,
              right: 24,
              background: 'rgba(255, 255, 255, 0.2)',
              border: 'none',
              color: '#ffffff',
              width: 44,
              height: 44,
              borderRadius: '50%',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 100000,
              transition: 'background 0.2s',
            }}
            aria-label="Close"
          >
            <X className="w-6 h-6" />
          </button>

          <img
            src={lightboxAsset.url}
            alt={lightboxAsset.altText || 'Sacred Darshan'}
            onClick={(e) => e.stopPropagation()}
            onError={(e) => {
              const baseName = lightboxAsset.rawSrc?.split("-").pop() || lightboxAsset.rawSrc?.split("/").pop() || lightboxAsset.url?.split("/").pop();
              if (baseName && !e.target.src.includes(`/images/${baseName}`)) {
                e.target.src = `/images/${baseName}`;
              } else {
                e.target.src = "/images/placeholder.jpg";
              }
            }}
            style={{
              maxWidth: '90vw',
              maxHeight: '88vh',
              objectFit: 'contain',
              display: 'block',
              borderRadius: 14,
              boxShadow: '0 10px 40px rgba(0, 0, 0, 0.8)',
            }}
          />
        </div>
      )}

      <HomamFooter />
    </div>
  );
}

export default HomamGallery;
